import { Suspense, createElement, type ReactElement } from "react";
import { Route } from "react-router-dom";
import RouteParamGuard from "./RouteParamGuard";
import type routes from "./routes";

/**
 * The `<Route>` children of the app's `<Routes>` (Router.tsx): one flat `<Route>` per path string of every route
 * entry. Its own module so the DS harness renders the app's routes exactly as Router does (the estimate overlay's
 * mount-once proof, S-V5-63, runs on it).
 *
 * The `key` is on the `<Route>` (react-router reads the children as a route list), never on what it renders:
 * `<Routes>` draws a matched entry as `RenderedRoute` → this element, so two entries whose element is the same
 * component render the same element type at the same position, and moving between their paths keeps that component
 * mounted (the estimate under its overlay viewer).
 */
export function routeElements(routeList: typeof routes): ReactElement[] {
  // KNOWN BUG, preserved verbatim: `singlePath` is a string out of the route's `path[]`, so `singlePath.props` is
  // always undefined. The two routes that declare `props` (`/95789` and `/invitation`, both `props: { formType }`)
  // therefore never receive them — the read should be off the route entry, not the path string. Left as-is; fixing
  // it would change behaviour.
  return routeList.flatMap((route: (typeof routeList)[number]) =>
    route.path.map((singlePath: string) => (
      <Route
        key={singlePath}
        path={singlePath}
        element={
          // Every route is guarded: each param a route DECLARES must satisfy its rule before the page mounts, so a
          // malformed link is refused here rather than reaching a service as `new ObjectId(<garbage>)`. A well-formed
          // link missing the query its page needs (a project page's `?projectId`, a portal list's `?byPortal=true`)
          // is replaced by the same link with it.
          <RouteParamGuard
            pattern={singlePath}
            params={"params" in route ? route.params : undefined}
            searchParams={route.searchParams}
            portalList={route.portalList}
          >
            <Suspense fallback={<></>}>
              {/* `element` is the union of every route's component, whose prop types are mutually incompatible, so
                  `createElement` cannot pick an overload; the intersection names a component-with-open-props
                  without widening `element`. */}
              {createElement(
                route.element as typeof route.element & React.ComponentType<Record<string, unknown>>,
                (singlePath as string & { props?: Record<string, unknown> }).props,
              )}
            </Suspense>
          </RouteParamGuard>
        }
      />
    )),
  );
}
